/* eslint-disable react/jsx-no-useless-fragment */
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { Field, Formik } from 'formik';
import { useEffect, useState } from 'react';
import Loader from '../../appLoader';
import { useToastHook } from '../../appToast';
import { useIntegrationMutation } from '../api';
import { goToLink } from '../helpers/integrationDetails.utils';
import {
  BottomSection,
  ConnectText,
  IconContainer,
  InfoBox,
  MiddleSection,
  Textline,
  TopSection,
} from './IntegrationModal.styled';
import { BiHelpCircle, BiInfoCircle } from 'react-icons/bi';
import { ItemBox } from '../helpers/integrationDetails.styled';
import { isHTML } from '../../shared/utils';
import ReactHtmlParser from 'html-react-parser';

export const IntegrationModal = ({
  handleIntegrateModalClose,
  isOpen,
  intDetailsData,
  handleConfirmModalOpen,
}: any) => {
  const [initialState, setInitialState] = useState<any>({});
  const [completeIntegration, setCompleteIntegration] =
    useState<boolean>(false);
  const { mutate: handleIntegration } = useIntegrationMutation();
  const [newToast] = useToastHook();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (intDetailsData?.dto?.fields) {
      const updatedInitialValues: any = {};
      intDetailsData.dto.fields.forEach((field: any) => {
        updatedInitialValues[field.key] = field.defaultValue || '';
      });
      setInitialState(updatedInitialValues);
    }
  }, [intDetailsData]);

  const handleHelpIconClick = () => {
    window.open(
      intDetailsData?.dto?.links?.helpDoc,
      '_blank',
      'toolbar=yes,scrollbars=yes,resizable=yes,top=270,left=800,width=700,height=600'
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleIntegrateModalClose}
      size="xl"
      isCentered
      motionPreset="slideInBottom"
    >
      <ModalOverlay />
      <ModalContent
        margin={0}
        rounded="lg"
        minH={completeIntegration ? '240px' : '400px'}
      >
        <ModalBody
          display="flex"
          flexDirection="column"
          justifyContent={'space-between'}
          padding="30px"
        >
          <>
            {completeIntegration ? (
              <Box
                w="100%"
                h="240px"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Box
                  width="100%"
                  height="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  You are redirected to {intDetailsData?.dto?.name} website.
                  Please complete the integration.
                </Box>
                <Button
                  width="100%"
                  color="#fff"
                  background="text.primary"
                  fontSize="lg"
                  fontWeight="normal"
                  fontFamily="heading"
                  borderRadius="lg"
                  lineHeight="130%"
                  onClick={handleIntegrateModalClose}
                >
                  {`Close`}
                </Button>
              </Box>
            ) : (
              <>
                <TopSection>
                  <InfoBox>
                    <IconContainer>
                      <Image src={intDetailsData?.dto?.links?.icon} />
                    </IconContainer>
                    <Flex alignItems={'center'} mt={'20px'}>
                      {intDetailsData?.dto?.name && (
                        <ConnectText>{`Connect to ${intDetailsData?.dto?.name}`}</ConnectText>
                      )}

                      {intDetailsData?.dto?.links?.helpDoc && (
                        <Box
                          as={BiHelpCircle}
                          onClick={handleHelpIconClick}
                          _hover={{ cursor: 'pointer', color: 'primary' }}
                          transition="color 0.3s"
                          lineHeight={'24px'}
                          fontSize={'xl'}
                          ml={'2px'}
                        />
                      )}
                    </Flex>
                  </InfoBox>
                  {(intDetailsData?.dto?.name === 'GitHub' ||
                    intDetailsData?.dto?.name === 'Bitbucket' ||
                    intDetailsData?.dto?.name === 'GitLab') && (
                    <Box
                      rounded="md"
                      p={2}
                      mt={1}
                      border="0.5px"
                      bgColor={'#eff5fe'}
                      alignContent={'center'}
                    >
                      <Flex alignItems={'center'}>
                        <BiInfoCircle
                          size={18}
                          color={'#3981f7'}
                          fontWeight={'bold'}
                        />
                        <Text color={'#3981f7'} ml={2} fontWeight={'bold'}>
                          Security: Your code is safe. We do not fetch, copy or
                          transfer.
                        </Text>
                      </Flex>
                      <Text ml="25px" fontSize={'sm'} fontFamily="heading">
                        DevDynamics doesn't access your code; it only uses the
                        metadata of pull requests, commits, issues, and
                        deployments. You can disconnect projects and
                        repositories anytime.
                      </Text>
                    </Box>
                  )}

                  {intDetailsData?.dto?.info &&
                  isHTML(intDetailsData?.dto?.info) ? (
                    <Box
                      textAlign={'center'}
                      mt={2}
                      mb={2}
                      fontFamily="heading"
                      fontSize="sm"
                      fontWeight="normal"
                      color="text.primary"
                      sx={{
                        a: {
                          color: 'primary',
                          textDecoration: 'underline',
                          _hover: {
                            color: 'primary',
                          },
                        },
                        code: {
                          backgroundColor: 'gray.100',
                          padding: '0.15em 0.4em',
                          borderRadius: 'md',
                        },
                      }}
                    >
                      {ReactHtmlParser(intDetailsData?.dto?.info)}
                    </Box>
                  ) : (
                    <Textline>{intDetailsData?.dto?.info}</Textline>
                  )}
                </TopSection>

                {intDetailsData?.dto?.fields &&
                  intDetailsData?.dto?.fields?.length >= 1 && (
                    <MiddleSection>
                      <Formik
                        initialValues={initialState}
                        enableReinitialize
                        onSubmit={(values) => {
                          setLoading(true);
                          const properties = Object.keys(values)?.map(
                            (key) => ({
                              name: key,
                              value: values[key],
                            })
                          );

                          handleIntegration(
                            {
                              name: intDetailsData?.dto?.name,
                              type: intDetailsData?.dto?.type,
                              active: true,
                              properties,
                            },
                            {
                              onSuccess: () => {
                                setLoading(false);
                                if (intDetailsData?.dto?.links?.oauth_url) {
                                  goToLink(
                                    intDetailsData?.dto?.links?.oauth_url
                                  );
                                  setCompleteIntegration(true);
                                } else {
                                  handleIntegrateModalClose();
                                  setTimeout(() => {
                                    handleConfirmModalOpen();
                                  }, 2000);
                                }
                              },
                              onError: (err: any) => {
                                setLoading(false);
                                newToast({
                                  status: 'error',
                                  message: err.message,
                                });
                              },
                            }
                          );
                        }}
                      >
                        {({ handleSubmit, errors, touched, values }: any) => (
                          <form onSubmit={handleSubmit}>
                            {intDetailsData?.dto?.fields?.map((field: any) => (
                              <FormControl
                                mb="10px"
                                isInvalid={
                                  !!errors[field.key] && touched[field.key]
                                }
                                key={field.key}
                              >
                                <FormLabel
                                  fontSize="sm"
                                  fontWeight="medium"
                                  color="text.secondary"
                                  lineHeight="24px"
                                >
                                  {field.field}
                                </FormLabel>

                                {field.type === 'Text' ? (
                                  <Field
                                    as={Input}
                                    placeholder={field.hint}
                                    id={field.key}
                                    name={field.key}
                                    type="text"
                                    validate={(value: any) => {
                                      let error;

                                      if (!value) {
                                        error = 'Field is mandatory.';
                                      }
                                      return error;
                                    }}
                                  />
                                ) : field.type.toLowerCase() === 'url' ? (
                                  <Field
                                    as={Input}
                                    placeholder={field?.hint}
                                    id={field?.key}
                                    name={field?.key}
                                    type={field?.type.toLowerCase()}
                                    validate={(value: any) => {
                                      let error;

                                      if (
                                        value &&
                                        !/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?([a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}|[0-9]+\.[0-9]+\.[0-9]+\.[0-9]+)(:[0-9]{1,5})?(\/.*)?$/.test(
                                          value
                                        )
                                      ) {
                                        error = 'Please enter a valid link.';
                                      } else if (value === '') {
                                        error = 'Field is mandatory.';
                                      }

                                      return error;
                                    }}
                                  />
                                ) : field.key === 'password' ? (
                                  <Field
                                    as={Input}
                                    placeholder={field?.hint}
                                    id={field?.key}
                                    name={field?.key}
                                    type={field.type}
                                    validate={(value: any) => {
                                      let error;
                                      if (value === '') {
                                        error = 'Field is mandatory.';
                                      }
                                      return error;
                                    }}
                                  />
                                ) : field.type === 'Checkbox' ? (
                                  <Field
                                    name={field.key}
                                    type="checkbox"
                                    as={Checkbox}
                                    validate={(value: any) => {
                                      let error;
                                      if (value === '') {
                                        if (field.key === 'consent') {
                                          error =
                                            'You must agree to the consent.';
                                        } else {
                                          error = 'Field is mandatory.';
                                        }
                                      }
                                      return error;
                                    }}
                                  >
                                    {field.hint}
                                  </Field>
                                ) : null}

                                <FormErrorMessage>
                                  {errors[field.key]}
                                </FormErrorMessage>
                              </FormControl>
                            ))}

                            <Button
                              type="submit"
                              w="full"
                              mt={'4'}
                              variant="tertiary"
                              isDisabled={
                                values?.consent !== undefined
                                  ? !values?.consent
                                  : false
                              }
                            >
                              {`Integrate ${intDetailsData?.dto?.name}`}
                            </Button>
                          </form>
                        )}
                      </Formik>
                    </MiddleSection>
                  )}

                {(intDetailsData?.dto?.fields === null ||
                  intDetailsData?.dto?.fields?.length < 1) && (
                  <BottomSection>
                    <Button
                      type="submit"
                      w="full"
                      mt={'4'}
                      variant="tertiary"
                      onClick={() => {
                        window.analytics.track('integration_created', {
                          properties: {
                            integration: `${intDetailsData?.dto?.name}`,
                            status: 'init',
                          },
                        });
                        goToLink(intDetailsData?.dto?.links?.oauth_url);
                        setCompleteIntegration((prev) => !prev);
                      }}
                    >
                      {`Integrate ${intDetailsData?.dto?.name}`}
                    </Button>
                  </BottomSection>
                )}
                {(loading || !intDetailsData) && <Loader />}
              </>
            )}
          </>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default IntegrationModal;
